import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Alert, Button, Divider, Form, FormInstance, Input } from 'antd';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { passwordRules } from '@lib/passwordRules';
import focusFirstFormError from '@lib/focusFirstFormError';
import useRuntimeConfig from '@vl-core/hooks/useConfig';
import Align from '@components/UI/utilities/Align';
import IdentityProviderButtons from '@components/Login/IdentityProviderButtons';
import SelfRegLink from '@components/Login/SelfRegLink';
import styled from '@emotion/styled';
import useArticle from 'vl-common/src/hooks/useArticle';
import { stripHTMLTags } from '@helpers/htmlUtils';
import { css } from '@emotion/react';
import { NotificationState } from '../../../pages/login';
import ContactLinksHint from '@components/Login/ContactLinksHint';
import UnstyledButton from '@components/UI/UnstyledButton';
import usePasswordVisibility from '@components/FormInputs/usePasswordVisibility';
import useIsNHS from '@hooks/useIsNHS';
import LoginMethodTooltip from '@components/Login/LoginMethodTooltip';

type LoginViewProps = {
  /** The Ant Design form instance */
  form: FormInstance<any>;
  /** A submit handler for the login form */
  onFinish: (values: any) => void;
  /** Loading state */
  loading: boolean;
  notification: NotificationState | null;
  removeNotification: () => void;
};

const AlertSection = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 1;
  @media (min-width: 1024px) {
    position: fixed;
    bottom: 0;
    right: 0;
    padding-right: 20px;
    padding-bottom: 48px;
    max-width: 400px;
    pointer-events: none;
    > * {
      pointer-events: auto;
    }
  }
`;

const DividerWithCentredText = ({ children }: { children: string }) => {
  const DividerCentralText = styled.div`
    position: relative;
    background: #fff;
    padding: 0 1em;
    width: auto;
    font-size: 16px;
    font-weight: 500;

    // Centre in Firefox
    display: table;
    margin: 0 auto;
  `;

  const DividerFullWidth = () => (
    <div aria-hidden style={{ flex: '1' }}>
      <Divider />
    </div>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', maxWidth: '100%' }}>
      <DividerFullWidth />
      <DividerCentralText>{children}</DividerCentralText>
      <DividerFullWidth />
    </div>
  );
};

const LoginView: FC<LoginViewProps> = ({ form, onFinish, loading, notification, removeNotification }) => {
  const { IDENTITY_PROVIDERS, SELF_REG, LUCY_ENV, PROD_PATIENT_URL, COGDR, COGNITO, CONTACT_US_URL } =
    useRuntimeConfig();
  const selfRegEnabled = SELF_REG;
  const { t } = useTranslation('common');
  const { query } = useRouter();
  const statusArticle = useArticle('system_warning', true);
  const isNHS = useIsNHS();
  const {
    visible: showPassword,
    toggleProps: passwordToggleProps,
    announcement: passwordVisibilityAnnouncement
  } = usePasswordVisibility();

  const isProd = LUCY_ENV.includes('prod');

  const { username: paramUsername } = query;

  const subheadingText = [
    'We’re pleased to provide online healthcare',
    isNHS ? 'on behalf of the NHS' : 'for AXA Health members'
  ]
    .map(t)
    .join(' ');

  return (
    <>
      <div className="login-card">
        <h1 style={{ fontWeight: 'bold' }}>{t('Welcome to HBSUK')}</h1>
        <p>{subheadingText}</p>
        <Divider />
        <AlertSection>
          {statusArticle?.body && !!stripHTMLTags(statusArticle?.body) && (
            <Alert
              type="warning"
              description={<div dangerouslySetInnerHTML={{ __html: statusArticle.body }} />}
              css={{ marginBottom: 20, width: '100%' }}
              closable
              showIcon
            />
          )}
          {!isProd && PROD_PATIENT_URL && (
            <Alert
              type="warning"
              message={t('This is a test environment')}
              description={
                <div>
                  {t('You are currently on a test environment.')}{' '}
                  <a tabIndex={0} href={PROD_PATIENT_URL}>
                    {t('The correct website can be found here')}
                  </a>
                  .
                </div>
              }
              css={{ marginBottom: 20, width: '100%' }}
              closable
              showIcon
            />
          )}
          {COGDR && (
            <Alert
              type="warning"
              message={t('System Recovery Mode')}
              description={t(
                'Please note the system is in recovery mode. If you are unable to log in please check your email for a new temporary password.'
              )}
              css={{ marginBottom: 20, width: '100%' }}
              showIcon
            />
          )}
        </AlertSection>
        {notification && (
          <Alert
            css={css`
              margin-bottom: 10px;
            `}
            message={notification.message}
            type={notification.type}
            showIcon
            closable
            onClose={removeNotification}
          />
        )}
        {IDENTITY_PROVIDERS?.providers && IDENTITY_PROVIDERS?.providers?.length > 0 && (
          <div>
            {/* eslint-disable-next-line test-selectors/button */}
            <IdentityProviderButtons />
          </div>
        )}
        {COGNITO && (
          <>
            {IDENTITY_PROVIDERS?.providers && IDENTITY_PROVIDERS?.providers?.length > 0 && (
              <DividerWithCentredText>{t('or')}</DividerWithCentredText>
            )}
            <div
              style={{
                marginBottom: selfRegEnabled ? undefined : 30
              }}
            >
              <strong style={{ marginRight: '4px' }}>{t('Log in with your HBSUK account details below')}</strong>
              <LoginMethodTooltip />
            </div>
            {selfRegEnabled && (
              <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginBottom: 20 }}>
                <SelfRegLink />
              </div>
            )}
            <Form
              name="basic"
              form={form}
              layout="vertical"
              onFinishFailed={focusFirstFormError(form)}
              {...{ onFinish }}
            >
              <Form.Item
                label={<span style={{ fontWeight: 'bold' }}>{t('Your Email Address')}</span>}
                name="username"
                initialValue={paramUsername}
                validateTrigger="onSubmit"
                rules={[
                  {
                    type: 'email',
                    message: 'Please input your e-mail address!'
                  },
                  {
                    required: true,
                    message: 'Please input your e-mail address!'
                  }
                ]}
              >
                <Input
                  type="email"
                  data-testid="login-email-input"
                  size="large"
                  autoComplete="off"
                  style={{ fontSize: '16px' }}
                />
              </Form.Item>

              <Form.Item
                label={<span style={{ fontWeight: 'bold' }}>{t('Password')}</span>}
                name="password"
                validateTrigger="onSubmit"
                rules={passwordRules}
                style={{ marginBottom: 0 }}
              >
                <Input
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password-input"
                  autoComplete="off"
                  size="large"
                  style={{ fontSize: '16px' }}
                  suffix={
                    <>
                      <UnstyledButton data-testid="toggle-password-visibility" {...passwordToggleProps}>
                        {showPassword ? (
                          <EyeOutlined aria-hidden="true" />
                        ) : (
                          <EyeInvisibleOutlined aria-hidden="true" />
                        )}
                      </UnstyledButton>
                      {passwordVisibilityAnnouncement}
                    </>
                  }
                />
              </Form.Item>
              <div style={{ marginBottom: 24 }}>
                <Link href="/forgot-password">{t('Forgot your password?')}</Link>
              </div>
              <Form.Item>
                <Align x="right">
                  <Button type="primary" block data-testid="log-in" htmlType="submit" size="large" {...{ loading }}>
                    {t('Login')}
                  </Button>
                </Align>
              </Form.Item>
            </Form>
          </>
        )}
        <div
          style={{
            display: 'flex',
            gap: 4,
            width: '100%',
            justifyContent: 'center',
            marginTop: COGNITO ? 0 : '1rem'
          }}
        >
          <p>{t('Get help and explore our services')}</p>
          <a
            href={CONTACT_US_URL}
            data-testid="contact us"
            target="_blank"
            rel="noreferrer"
            style={{ fontWeight: 600, textDecoration: 'underline' }}
          >
            {t('here')}
          </a>
        </div>
      </div>

      <ContactLinksHint paddingTop />
    </>
  );
};

export default LoginView;
